import React from "react";
import { Controller } from "react-hook-form";
import PropTypes from "prop-types";
import { Tooltip, Box } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

import InputWrapper from "./InputWrapper";
import ReadOnlyInput from "./ReadOnlyInput";
import CustomInputField from "./CustomInputField";

import { DEFAULT_SIZE } from "../../../utils/constant";

export default function CustomFormTextInput({
  name,
  mode = "edit",
  label,
  placeholder,
  rules,
  control,
  type = "text",
  fieldProps = {},
  size = DEFAULT_SIZE,
  startAdornment = null,
  isAddMode,
  disabled,
  onCustomBlur,
  withInfoIcon = false,
  infoMessage = "",
  labelAdornment = null,
  ...rest
}) {
  const {
    canView = false,
    canAdd = false,
    canEdit = false
  } = rest.permission || {};

  if (!canView || (isAddMode && !canAdd)) {
    return null;
  }

  const editModeAllowed = isAddMode ? canAdd : canEdit;

  if (
    mode === "read" ||
    (mode === "edit" && !editModeAllowed)
  ) {
    return (
      <ReadOnlyInput
        type={type}
        size={size}
        name={name}
        label={label}
        labelAdornment={labelAdornment}
        {...rest}
        rules={rules}
      />
    );
  }

  const handleKeyDown = eventObj => {
    if (
      type === "number" &&
      (eventObj.key === "e" || eventObj.key === "E")
    ) {
      eventObj.preventDefault();
      eventObj.stopPropagation();
    }
  };

  const labelText = rules?.required ? `${label} *` : label;
  const shouldShowIcon = (withInfoIcon && infoMessage) || labelAdornment;

  return (
    <InputWrapper size={size}>
      {shouldShowIcon && (
        <Box sx={{display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px"}}>
          <span style={{fontSize: "12px", fontWeight: "500", color: "rgb(102,112,133)", letterSpacing: "0.3px"}}>
            {label}
            {rules?.required && (
              <span style={{ color: "#d32f2f" }}> *</span>
            )}
          </span>

          {labelAdornment}

          {withInfoIcon && infoMessage && (
            <Tooltip title={infoMessage} arrow placement="top" enterDelay={200}>
              <InfoIcon sx={{fontSize: 18, color: "#6B7280", cursor: "help",
                  "&:hover": {
                    color: "#1F88E6"
                  }
                }}
              />
            </Tooltip>
          )}
        </Box>
      )}

      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({
          field: { onChange, onBlur, value = "" },
          fieldState: { error }
        }) => (
          <CustomInputField
            value={value}
            onChange={eventObj => {
              onChange(eventObj.target.value); 
              fieldProps?.onChange?.(eventObj);
            }}
            onBlur={eventObj => {
              const trimmedValue = eventObj.target.value.trim();

              if (value !== trimmedValue) {
                onChange(trimmedValue);
              }

              if (typeof onCustomBlur === "function") {
                onCustomBlur(eventObj);
              }

              fieldProps?.onBlur?.(eventObj);

              onBlur();
            }}
            error={error}
            helperText={error?.message || null}
            label={shouldShowIcon ? "" : labelText}
            placeholder={placeholder}
            type={type}
            disabled={disabled}
            startAdornment={startAdornment}
            multiline={rest.multiline}
            minRows={rest.minRows}
            fieldProps={fieldProps}
            onKeyDown={handleKeyDown}
          />
        )}
      />
    </InputWrapper>
  );
}

CustomFormTextInput.propTypes = {
  name: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["read", "edit"]).isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  rules: PropTypes.object.isRequired,
  startAdornment: PropTypes.node,
  control: PropTypes.object.isRequired,
  type: PropTypes.string,
  isAddMode: PropTypes.bool,
  fieldProps: PropTypes.object,
  permission: PropTypes.shape({
    canView: PropTypes.bool,
    canAdd: PropTypes.bool,
    canEdit: PropTypes.bool
  }),
  size: PropTypes.string,
  disabled: PropTypes.bool,
  withInfoIcon: PropTypes.bool,
  infoMessage: PropTypes.string,
  labelAdornment: PropTypes.node,
};